import { AlertCircleIcon, CheckIcon, InfoCircleIcon, WarningTriangleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertDescription } from '../alert/alert.description';
import { AlertTitle } from '../alert/alert.title';
import { AlertLevel } from '../alert/types';

const toastIcons: Record<AlertLevel, React.ReactNode> = {
  [AlertLevel.Default]: <InfoCircleIcon size={20} className="text-quinary" />,
  [AlertLevel.Info]: <InfoCircleIcon size={20} className="text-quinary" />,
  [AlertLevel.Error]: <AlertCircleIcon size={20} className="text-error-400" />,
  [AlertLevel.Success]: <CheckIcon size={20} className="text-success-400" />,
  [AlertLevel.Warning]: <WarningTriangleIcon size={20} className="text-warning-400" />,
};

export interface ToastData {
  id: string;
  title: string;
  description?: string;
  level?: AlertLevel;
  timeout?: number;
}

interface ToastContextType {
  addToast: (toast: Omit<ToastData, 'id'>) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_FADE_DURATION_MS = 1000;

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [fadingIds, setFadingIds] = useState<ReadonlySet<string>>(new Set());
  const timersRef = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timerId = timersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
    setFadingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const fadeOutToast = useCallback(
    (id: string) => {
      setFadingIds((prev) => new Set(prev).add(id));
      const timerId = setTimeout(() => removeToast(id), TOAST_FADE_DURATION_MS);
      timersRef.current.set(id, timerId as unknown as number);
    },
    [removeToast],
  );

  const addToast = useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: ToastData = {
        ...toast,
        id,
        timeout: toast.timeout || 5000,
      };

      setToasts((prev) => [...prev, newToast]);

      const timerId = setTimeout(() => {
        fadeOutToast(id);
      }, newToast.timeout);

      timersRef.current.set(id, timerId as unknown as number);
    },
    [fadeOutToast],
  );

  useEffect(() => {
    return () => {
      for (const timerId of timersRef.current.values()) {
        clearTimeout(timerId);
      }
      timersRef.current.clear();
    };
  }, []);

  const contextValue = React.useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm" aria-label="Notifications">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              fadingIds.has(toast.id)
                ? 'animate-out fade-out fill-mode-forwards duration-1000 motion-reduce:animate-none'
                : 'animate-in slide-in-from-right-full duration-300',
            )}
          >
            <div className="flex items-start gap-8 rounded-xl bg-foreground-primary p-8 shadow-lg" role="alert">
              <div className="h-fit w-fit shrink-0">{toastIcons[toast.level || AlertLevel.Info]}</div>
              <div className="flex flex-col gap-2 justify-center">
                <AlertTitle className="text-primary-on-brand">{toast.title}</AlertTitle>
                {toast.description && <AlertDescription className="text-quinary">{toast.description}</AlertDescription>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
